/**
 * Universal runtime translator with **auto-DOM-walk**.
 *
 * Strategy:
 *  - Watch the entire #root for text-node mutations.
 *  - For each visible English text node:
 *      • if its translation is cached → swap immediately.
 *      • otherwise → queue + batch-POST to /api/i18n/translate.
 *      • when the result returns, swap.
 *  - Cache is persisted in localStorage forever.
 *
 * This means picking PL or ES from the landing-page selector translates the
 * WHOLE app — sidebars, modals, forms, errors — without us wrapping every
 * literal in components.
 *
 * Limits / safeguards:
 *  - Pure-numeric / URL / symbol-only strings are skipped.
 *  - Input values and contenteditable nodes are NEVER touched.
 *  - Original English is preserved on the node via `__i18nSrc`, so we always
 *    re-translate from the source (not from a prior PL → ES round-trip).
 */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { api } from './api';
import { useAuth } from './auth';

const CACHE_KEY = (locale) => `i18n_cache_${locale}`;
const SOURCE_LOCALE = 'en';

const TrContext = createContext({
  locale: 'en',
  tr: (s) => s,
});

// In-module mutable cache (single-page; shared across providers).
const cache = {};   // { 'pl': {en: 'pl'}, 'es': {...} }
const pending = new Set();
let flushTimer = null;
let currentLocale = SOURCE_LOCALE;

function loadCacheFor(loc) {
  try {
    const raw = localStorage.getItem(CACHE_KEY(loc));
    cache[loc] = raw ? JSON.parse(raw) : {};
  } catch { cache[loc] = {}; }
}
function persistCache(loc) {
  try { localStorage.setItem(CACHE_KEY(loc), JSON.stringify(cache[loc] || {})); } catch { /* ignore */ }
}

function shouldTranslate(text) {
  if (!text || typeof text !== 'string') return false;
  const t = text.trim();
  if (!t || t.length < 2) return false;
  if (/^[\d\s.,:;/$€£¥%\-+()*[\]{}<>=|&·•—–_]+$/.test(t)) return false;
  if (/^https?:\/\//.test(t)) return false;
  // Skip obvious code identifiers / data testids / file paths
  if (/^[a-z]+_[a-z_]+$/.test(t)) return false;
  return true;
}

async function flushBatch(refresh) {
  if (!pending.size || currentLocale === SOURCE_LOCALE) return;
  const allPending = Array.from(pending);
  pending.clear();
  const batch = allPending.slice(0, 80);
  const overflow = allPending.slice(80);
  try {
    const { data } = await api.post('/i18n/translate', { texts: batch, target_locale: currentLocale });
    cache[currentLocale] = cache[currentLocale] || {};
    Object.assign(cache[currentLocale], data.translations || {});
    persistCache(currentLocale);
    if (refresh) refresh();
  } catch (e) {
    // silent — UI keeps the English fallback
  }
  if (overflow.length) {
    overflow.forEach((s) => pending.add(s));
    flushTimer = setTimeout(() => flushBatch(refresh), 80);
  }
}

function translateString(s) {
  if (currentLocale === SOURCE_LOCALE) return s;
  if (!shouldTranslate(s)) return s;
  const key = s.trim();
  const cached = cache[currentLocale]?.[key];
  if (cached) {
    // Preserve any leading / trailing whitespace from the original.
    return s.replace(key, cached);
  }
  if (!pending.has(key)) {
    pending.add(key);
  }
  return s;
}

// --- DOM walker ---

const SRC_PROP = '__i18nSrc';

// True when `val` is one of OUR OWN cached translations of `src` (any locale).
// Used to distinguish "we translated this node" from "React changed its text".
function isOurTranslation(src, val) {
  const key = (src || '').trim();
  const v = (val || '').trim();
  if (!key || !v || key === v) return false;
  return Object.values(cache).some((m) => m && m[key] === v);
}

function isSkipNode(node) {
  if (!node || node.nodeType !== Node.TEXT_NODE) return true;
  const p = node.parentElement;
  if (!p) return true;
  const tag = p.tagName;
  if (!tag) return true;
  if (['SCRIPT', 'STYLE', 'NOSCRIPT', 'CODE', 'PRE', 'TEXTAREA', 'INPUT', 'KBD', 'SAMP'].includes(tag)) return true;
  if (p.isContentEditable) return true;
  if (p.closest('[data-no-translate]')) return true;
  return false;
}

function walkAndTranslate(root, refresh) {
  if (!root) return;
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
    acceptNode: (n) => isSkipNode(n) ? NodeFilter.FILTER_REJECT : NodeFilter.FILTER_ACCEPT,
  });
  let n;
  let queued = false;
  while ((n = walker.nextNode())) {
    // Preserve original English source so we always translate from source.
    if (n[SRC_PROP] == null) {
      n[SRC_PROP] = n.nodeValue;
    } else if (n.nodeValue !== n[SRC_PROP] && !isOurTranslation(n[SRC_PROP], n.nodeValue)) {
      // React updated this text node IN PLACE (e.g. a status label flipping
      // "Saving…" → "Saved ✓"). Adopt the new value as the source — otherwise
      // we would forever revert React's update to the stale snapshot.
      n[SRC_PROP] = n.nodeValue;
    }
    const src = n[SRC_PROP];
    if (currentLocale === SOURCE_LOCALE) {
      if (n.nodeValue !== src) n.nodeValue = src;
      continue;
    }
    const out = translateString(src);
    if (n.nodeValue !== out) n.nodeValue = out;
    if (out === src && shouldTranslate(src)) queued = true;
  }
  // Also translate attribute values that face the user: placeholder, title, alt, aria-label.
  const attrSelector = '[placeholder],[title],[alt],[aria-label]';
  root.querySelectorAll && root.querySelectorAll(attrSelector).forEach((el) => {
    ['placeholder', 'title', 'alt', 'aria-label'].forEach((a) => {
      if (!el.hasAttribute(a)) return;
      const srcKey = `__i18n_attr_src_${a}`;
      const cur = el.getAttribute(a);
      if (!el[srcKey]) el[srcKey] = cur;
      const src = el[srcKey];
      if (currentLocale === SOURCE_LOCALE) {
        if (cur !== src) el.setAttribute(a, src);
        return;
      }
      const out = translateString(src);
      if (cur !== out) el.setAttribute(a, out);
      if (out === src && shouldTranslate(src)) queued = true;
    });
  });

  if (queued && pending.size) {
    if (flushTimer) clearTimeout(flushTimer);
    flushTimer = setTimeout(() => flushBatch(refresh), 100);
  }
}

export function TrProvider({ children }) {
  const { locale } = useAuth();
  const [tick, setTick] = useState(0);
  const [enabled, setEnabled] = useState(true);
  const observerRef = useRef(null);

  // Keep module-level currentLocale in sync. Disable when admin toggles feature off.
  currentLocale = enabled ? locale : SOURCE_LOCALE;

  const refresh = useCallback(() => setTick((t) => t + 1), []);

  // Bootstrap caches when provider mounts. Also peek at clinic settings —
  // honor feature_runtime_i18n (defaults to ON if missing).
  useEffect(() => {
    loadCacheFor('pl');
    loadCacheFor('es');
    fetch('/api/clinic/settings', { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => { if (j && j.feature_runtime_i18n === false) setEnabled(false); })
      .catch(() => {});
  }, []);

  // Run a translation pass whenever locale changes or tick increments.
  useEffect(() => {
    const root = document.getElementById('root') || document.body;
    walkAndTranslate(root, refresh);
  }, [locale, tick, refresh]);

  // Observe DOM mutations and translate as content appears.
  useEffect(() => {
    const root = document.getElementById('root') || document.body;
    if (!root) return;
    const obs = new MutationObserver((mutations) => {
      // Throttle: a tiny debounce so we don't run on every keystroke.
      if (obs._timer) return;
      obs._timer = setTimeout(() => {
        obs._timer = null;
        walkAndTranslate(root, refresh);
      }, 60);
    });
    obs.observe(root, { childList: true, characterData: true, subtree: true });
    observerRef.current = obs;
    return () => obs.disconnect();
  }, [refresh]);

  const tr = useCallback((text) => {
    if (locale === SOURCE_LOCALE || !text) return text;
    const out = translateString(text);
    // schedule flush for queued strings
    if (pending.size) {
      if (flushTimer) clearTimeout(flushTimer);
      flushTimer = setTimeout(() => flushBatch(refresh), 80);
    }
    return out;
  }, [locale, refresh]);

  const value = useMemo(() => ({ locale, tr }), [locale, tr]);
  return <TrContext.Provider value={value}>{children}</TrContext.Provider>;
}

export function useTr() {
  return useContext(TrContext);
}

/** Drop-in component (still useful for cases where you want explicit control). */
export function T({ children }) {
  const { tr } = useTr();
  if (typeof children !== 'string') return children;
  return tr(children);
}
